<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { omit } from 'lodash-es';
import type { ApplicantEntry, ApplicantTypeEnum } from '@/types/Applicant';
import type { BorrowerGroup, CompanyDetails, TrustDetails } from '@/types/BorrowerGroup';
import { ApplicantType, BorrowerType, BorrowerTypeOptions, TrusteeType } from '@/helpers/const';
import type { BorrowerTypeEnum } from '@/helpers/const';
import { BorrowerRole, type BorrowerRoleEnum } from '@/types/BorrowerGroup';
import { mapToApplicants } from '@/helpers/builder';
import { getFullName } from '@/helpers/format';
import { useBrokerLoanAppProcess } from '@/helpers/useBrokerLoanAppProcess';
import { useProductType } from '@/helpers/useProductType';
import { ToolTip } from '@/helpers/tippy';
import { applicantSchema } from '@/validation';
import { TrashIcon } from '@heroicons/vue/24/outline';
import IndividualForm from './IndividualForm.vue';
import CompanySection from './CompanySection.vue';
import TrustSection from './TrustSection.vue';

const props = withDefaults(
  defineProps<{
    modelValue: ApplicantEntry;
    type: ApplicantTypeEnum;
    primary?: ApplicantEntry;
    allIndividuals?: ApplicantEntry[];
    currentId?: string;
    borrowerIndex: number;
    initialBorrowerGroup?: BorrowerGroup;
    forceShowErrors?: boolean;
    excludedIndividualBorrowerIds?: string[];
  }>(),
  {
    primary: undefined,
    allIndividuals: () => [],
    currentId: '',
    borrowerIndex: 0,
    initialBorrowerGroup: undefined,
    forceShowErrors: false,
    excludedIndividualBorrowerIds: () => [],
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', v: ApplicantEntry): void;
  (e: 'edit', id: string): void;
  (e: 'remove', id: string): void;
  (e: 'relationship-change', individualId: string, newStatus: string, oldStatus?: string): void;
  (e: 'sync-applicant', applicant: ApplicantEntry): void;
  (e: 'remove-applicant', applicantId: string): void;
}>();

const { loanApp } = useBrokerLoanAppProcess();
const { isCommercialProduct } = useProductType(loanApp);

// Determine if card should start collapsed based on existing data
const hasInitialData = computed(() => {
  if (props.initialBorrowerGroup?.type === BorrowerType.INDIVIDUAL) {
    return Boolean(props.modelValue.firstName && props.modelValue.lastName);
  }
  if (props.initialBorrowerGroup?.type === BorrowerType.COMPANY) {
    return Boolean(props.initialBorrowerGroup.companyDetails?.companyName);
  }
  if (props.initialBorrowerGroup?.type === BorrowerType.TRUST) {
    return Boolean(props.initialBorrowerGroup.trustDetails?.trustName);
  }
  return false;
});

// Filter borrower type options - remove Individual for commercial loans
const availableBorrowerTypeOptions = computed(() => {
  if (!isCommercialProduct.value) return BorrowerTypeOptions;
  return omit(BorrowerTypeOptions, BorrowerType.INDIVIDUAL);
});

const borrowerTypeLabel = computed(() => {
  return isCommercialProduct.value
    ? 'Is the borrower a company or trust?'
    : 'Is the borrower an individual, company or trust?';
});

const isNonIndividualBorrower = (type?: BorrowerTypeEnum): boolean => {
  return type === BorrowerType.COMPANY || type === BorrowerType.TRUST;
};

// Determine initial card expansion state
// - Company/Trust: Always expanded to show directors/trustees
// - Individual: Expanded only if no saved data exists
const initialShowEntry = isNonIndividualBorrower(props.initialBorrowerGroup?.type) || !hasInitialData.value;
const showEntry = ref(initialShowEntry);

const SCROLL_DELAY_MS = 300;

const selectedBorrowerType = ref<BorrowerTypeEnum | undefined>(props.initialBorrowerGroup?.type);

const initialCompanyDetails = computed<CompanyDetails>(() => props.initialBorrowerGroup?.companyDetails ?? {});
const initialTrustDetails = computed<TrustDetails>(() => props.initialBorrowerGroup?.trustDetails ?? {});
const initialTrustCompanyDetails = computed<CompanyDetails>(
  () => props.initialBorrowerGroup?.trustDetails?.companyDetails ?? {},
);

const getApplicantsByRole = (role: BorrowerRoleEnum): ApplicantEntry[] => {
  if (!props.initialBorrowerGroup?.applicants) return [];
  return props.initialBorrowerGroup.applicants
    .filter((a) => a.role === role)
    .map((a) => props.allIndividuals.find((i) => i.id === a.applicantId))
    .filter((entry): entry is ApplicantEntry => Boolean(entry));
};

const initialDirectors = computed(() => getApplicantsByRole(BorrowerRole.DIRECTOR));
const initialTrustees = computed(() => getApplicantsByRole(BorrowerRole.TRUSTEE));

const individualFormRef = ref<InstanceType<typeof IndividualForm>>();
const companySectionRef = ref<InstanceType<typeof CompanySection>>();
const trustSectionRef = ref<InstanceType<typeof TrustSection>>();

const isIndividual = computed(() => selectedBorrowerType.value === BorrowerType.INDIVIDUAL);
const isTrust = computed(() => selectedBorrowerType.value === BorrowerType.TRUST);
const isCompany = computed(() => selectedBorrowerType.value === BorrowerType.COMPANY);
const isPrimaryBorrower = computed(() => props.borrowerIndex === 0);

const individual = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const borrowerNumber = computed(() => props.borrowerIndex + 1);

const actionSection = ref<HTMLElement>();
const editItem = () => {
  showEntry.value = true;
  if (isIndividual.value) {
    individualFormRef.value?.resetForm({ values: { ...props.modelValue, type: props.type } });
  }
};

const expandAndFocus = () => {
  editItem();
  setTimeout(() => {
    actionSection.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    });
  }, SCROLL_DELAY_MS);
};

// Expand a specific individual within this BorrowerCard (could be the borrower itself or a nested director/trustee)
const expandIndividual = (individualId: string): boolean => {
  if (isIndividual.value && props.modelValue.id === individualId) {
    expandAndFocus();
    return true;
  }
  if (isCompany.value) {
    return companySectionRef.value?.expandDirector(individualId) ?? false;
  }
  if (isTrust.value) {
    return trustSectionRef.value?.expandIndividual(individualId) ?? false;
  }
  return false;
};

const fullName = computed(() => getFullName(props.modelValue));

// Display name based on borrower type - delegates to section components
// Falls back to initial data when refs aren't mounted yet
const displayName = computed(() => {
  if (isIndividual.value) {
    return fullName.value;
  }
  if (isCompany.value) {
    return companySectionRef.value?.displayName || props.initialBorrowerGroup?.companyDetails?.companyName || '';
  }
  if (isTrust.value) {
    return trustSectionRef.value?.displayName || props.initialBorrowerGroup?.trustDetails?.trustName || '';
  }
  return fullName.value;
});

// Check if company/trust basic details are saved (delegates to section components)
// Falls back to initial data when refs aren't mounted yet
const isSectionSaved = computed(() => {
  if (isCompany.value) {
    return companySectionRef.value?.isCompanySaved ?? !!props.initialBorrowerGroup?.companyDetails?.companyName;
  }
  if (isTrust.value) {
    // Check both company trustee saved or individual trustee basic form saved
    const refSaved = trustSectionRef.value?.isCompanySaved || trustSectionRef.value?.isTrustBasicSaved;
    return refSaved ?? !!props.initialBorrowerGroup?.trustDetails?.trustName;
  }
  return false;
});

const saveChanges = async () => {
  // Short-circuit: already saved and collapsed
  if (!showEntry.value) {
    const isAlreadySaved = isIndividual.value
      ? props.modelValue.firstName && props.modelValue.lastName
      : isSectionSaved.value;
    if (isAlreadySaved) return true;
  }

  const sectionRef = isIndividual.value
    ? individualFormRef.value
    : isCompany.value
    ? companySectionRef.value
    : trustSectionRef.value;

  const saved = (await sectionRef?.update()) ?? false;
  // Only collapse for Individual borrowers - Company/Trust should stay expanded to show directors/trustees
  if (saved && isIndividual.value) showEntry.value = false;
  return saved;
};

const cancelChanges = () => {
  const sectionRef = isIndividual.value
    ? individualFormRef.value
    : isCompany.value
    ? companySectionRef.value
    : trustSectionRef.value;
  sectionRef?.cancel();
  // Only collapse for Individual borrowers - Company/Trust should stay expanded to show directors/trustees
  if (isIndividual.value) showEntry.value = false;
};

const editSectionDetails = () => {
  if (isCompany.value) {
    companySectionRef.value?.editCompanyDetails();
  } else if (isTrust.value) {
    // Edit company details if company trustee, or trust details if individual trustee
    if (trustSectionRef.value?.isCompanySaved) {
      trustSectionRef.value?.editCompanyDetails();
    } else if (trustSectionRef.value?.isTrustBasicSaved) {
      trustSectionRef.value?.editTrustDetails();
    }
  }
  showEntry.value = true;
};

const showModal = ref(false);
const openModal = () => {
  showModal.value = true;
};

const removeItem = () => {
  showModal.value = false;
  emit('remove', props.modelValue.id);
};

const getBorrowerGroup = (): BorrowerGroup | null => {
  if (!selectedBorrowerType.value) return null;

  const borrowerGroup: BorrowerGroup = {
    id: `borrower-${props.borrowerIndex}`,
    type: selectedBorrowerType.value,
  };

  if (isIndividual.value) {
    borrowerGroup.applicants = [
      {
        id: `borrower-${props.borrowerIndex}-individual`,
        applicantId: props.modelValue.id,
        role: BorrowerRole.INDIVIDUAL,
      },
    ];
  } else if (isCompany.value && companySectionRef.value) {
    const companyData = companySectionRef.value.companyDetails;
    const directorsData = companySectionRef.value.directors || [];

    borrowerGroup.companyDetails = companyData ? { ...companyData } : undefined;
    borrowerGroup.entityName = companyData?.companyName;
    borrowerGroup.applicants = mapToApplicants(directorsData, BorrowerRole.DIRECTOR);
  } else if (isTrust.value && trustSectionRef.value) {
    const trustData = trustSectionRef.value.trustDetails;
    const companyData = trustSectionRef.value.companyDetails;
    const directorsData = trustSectionRef.value.directors || [];
    const trusteesData = trustSectionRef.value.trustees || [];

    borrowerGroup.trustDetails = trustData
      ? {
          ...trustData,
          companyDetails: trustData?.trusteeType === TrusteeType.COMPANY ? { ...companyData } : undefined,
        }
      : undefined;
    borrowerGroup.entityName = trustData?.trustName;

    if (trustData?.trusteeType === TrusteeType.COMPANY) {
      borrowerGroup.applicants = mapToApplicants(directorsData, BorrowerRole.DIRECTOR);
    } else if (trustData?.trusteeType === TrusteeType.INDIVIDUAL) {
      borrowerGroup.applicants = mapToApplicants(trusteesData, BorrowerRole.TRUSTEE);
    }
  }

  return borrowerGroup;
};

const hasSavedName = (applicant: ApplicantEntry) => Boolean(applicant.firstName && applicant.lastName);

// Get all applicants (directors/trustees) from this card for the main applicants array
const getApplicants = (): ApplicantEntry[] => {
  if (isIndividual.value) {
    return [props.modelValue];
  }
  if (isCompany.value && companySectionRef.value) {
    return companySectionRef.value.directors?.filter(hasSavedName) ?? [];
  }
  if (isTrust.value && trustSectionRef.value) {
    const trustData = trustSectionRef.value.trustDetails;
    if (trustData?.trusteeType === TrusteeType.COMPANY) {
      return trustSectionRef.value.directors?.filter(hasSavedName) ?? [];
    }
    return trustSectionRef.value.trustees?.filter(hasSavedName) ?? [];
  }
  return [];
};

// Reactive computed for all individuals in this card (used for relationship management)
const individuals = computed((): ApplicantEntry[] => {
  if (isIndividual.value) {
    return [props.modelValue];
  }
  if (isCompany.value && companySectionRef.value) {
    return companySectionRef.value.directors ?? [];
  }
  if (isTrust.value && trustSectionRef.value) {
    const trustData = trustSectionRef.value.trustDetails;
    if (trustData?.trusteeType === TrusteeType.COMPANY) {
      return trustSectionRef.value.directors ?? [];
    }
    return trustSectionRef.value.trustees ?? [];
  }
  return [];
});

const iconName = computed(() => {
  if (isCompany.value) {
    return 'company';
  }
  if (isTrust.value) {
    return 'trust';
  }
  return 'customer';
});

watch(
  () => props.modelValue,
  async (val) => {
    if (!val.firstName && !val.lastName) {
      showEntry.value = !(await applicantSchema(props.primary).isValid(val));
    }
  },
  { immediate: true },
);

watch(
  () => props.allIndividuals,
  (individuals) => {
    // Only run if borrower type not selected yet
    if (selectedBorrowerType.value || isCommercialProduct.value) return;

    const validIndividuals = individuals.filter((i) => i.firstName && i.lastName);

    // If only ONE individual exists → auto select
    if (validIndividuals.length === 1) {
      selectedBorrowerType.value = BorrowerType.INDIVIDUAL;
    }
  },
  { immediate: true },
);

defineExpose({
  update: saveChanges,
  expandAndFocus,
  expandIndividual,
  getBorrowerGroup,
  getApplicants,
  selectedBorrowerType,
  individuals,
});
</script>

<template>
  <div :class="{ 'mb-5': props.type === ApplicantType.SECONDARY }">
    <BxExpander
      v-model="showEntry"
      :header-background="isSectionSaved ? 'white' : 'none'"
      :separator="!isSectionSaved"
      overflow-visible
    >
      <template #header>
        <div class="px-4 py-1 sm:px-6">
          <!-- Company/Trust saved: Show name with Edit/Remove in header (collapsed style) -->
          <template v-if="isSectionSaved">
            <div class="-ml-4 flex flex-wrap items-center justify-between sm:flex-nowrap">
              <div class="w-100 ml-4">
                <h3 class="w-100 flex items-center text-lg font-medium leading-6 text-gray-900">
                  <div class="mr-3 hidden md:block"><BxIcon :name="iconName" /></div>
                  <div>{{ displayName }}</div>
                </h3>
              </div>
              <div class="mb-3 ml-4 mt-4 flex-shrink-0">
                <BxButton
                  :data-test-id="`${props.modelValue.id}-borrower-edit`"
                  variant="secondary"
                  @click="editSectionDetails()"
                  >Edit</BxButton
                >
                <BxButton
                  :data-test-id="`${props.modelValue.id}-borrower-remove`"
                  variant="link"
                  class="ml-4"
                  :disabled="props.type !== ApplicantType.SECONDARY"
                  @click="openModal()"
                >
                  Remove
                </BxButton>
              </div>
            </div>
          </template>
          <!-- No display name yet (new borrower) -->
          <template v-else-if="!displayName">
            <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
              <h3 class="flex items-center py-4 text-lg font-medium leading-6 text-gray-900">
                <span class="mr-3 hidden md:block">
                  <BxIcon :name="iconName" />
                </span>
                <span>{{ displayName || `Borrower ${borrowerNumber}` }}</span>
              </h3>
            </div>
          </template>
          <!-- Editing mode with display name -->
          <template v-else-if="showEntry">
            <h3 class="flex items-center py-4 text-lg font-medium leading-6 text-gray-900">
              <span class="mr-3 hidden md:block">
                <BxIcon :name="iconName" />
              </span>
              <span>{{ displayName }}</span>
            </h3>
          </template>
          <!-- Individual saved: collapsed view with Edit/Remove -->
          <template v-else>
            <div class="-ml-4 flex flex-wrap items-center justify-between sm:flex-nowrap">
              <div class="w-100 ml-4">
                <h3 class="w-100 flex items-center text-lg font-medium leading-6 text-gray-900">
                  <div class="mr-3 hidden md:block"><BxIcon :name="iconName" /></div>
                  <div>{{ displayName }}</div>
                </h3>
              </div>
              <div class="mb-3 ml-4 mt-4 flex-shrink-0">
                <BxButton :data-test-id="`${props.modelValue.id}-borrower-edit`" variant="secondary" @click="editItem()"
                  >Edit</BxButton
                >
                <BxButton
                  :data-test-id="`${props.modelValue.id}-borrower-remove`"
                  variant="link"
                  class="ml-4"
                  :disabled="props.type !== ApplicantType.SECONDARY"
                  @click="openModal()"
                >
                  Remove
                </BxButton>
              </div>
            </div>
          </template>
        </div>
      </template>
      <template #body>
        <div class="p-6 pt-0">
          <!-- Borrower Type Dropdown - hidden once section is saved -->
          <div v-if="!isSectionSaved" class="form-group mb-3 pt-6" :class="{ 'min-h-[160px]': !selectedBorrowerType }">
            <h4 class="mb-6 font-medium">{{ borrowerTypeLabel }}</h4>
            <div class="form-control relative pr-4">
              <BxSelect
                :id="modelValue.id + '-borrower-type'"
                v-model="selectedBorrowerType"
                :data-test-id="`${modelValue.id}-borrower-type-select`"
                :list-items="availableBorrowerTypeOptions"
                label="Borrower type"
                label-type="floating"
                placeholder="Select borrower type..."
              />
              <ToolTip
                v-if="isCommercialProduct"
                content="Individual or Sole Trader structures are reserved for Residential products. Commercial loans require a corporate borrowing entity."
              >
                <BxIcon name="circleInfoLight" class="absolute -right-6 top-[45%] -translate-y-1/2 cursor-pointer" />
              </ToolTip>
            </div>
          </div>

          <IndividualForm
            v-if="isIndividual"
            ref="individualFormRef"
            v-model="individual"
            :type="props.type"
            :primary="props.primary"
            :all-individuals="props.allIndividuals"
            :current-id="props.currentId"
            :force-show-errors="props.forceShowErrors"
            :excluded-applicant-ids="props.excludedIndividualBorrowerIds"
            @relationship-change="(individualId: string, newStatus: string, oldStatus?: string) => emit('relationship-change', individualId, newStatus, oldStatus)"
            @sync-applicant="(applicant: ApplicantEntry) => emit('sync-applicant', applicant)"
          />

          <CompanySection
            v-else-if="isCompany"
            ref="companySectionRef"
            :all-individuals="props.allIndividuals"
            :initial-company-details="initialCompanyDetails"
            :initial-directors="initialDirectors"
            :is-primary-borrower="isPrimaryBorrower"
            :force-show-errors="props.forceShowErrors"
            @relationship-change="(individualId: string, newStatus: string, oldStatus?: string) => emit('relationship-change', individualId, newStatus, oldStatus)"
            @sync-applicant="(applicant: ApplicantEntry) => emit('sync-applicant', applicant)"
            @remove-applicant="(applicantId: string) => emit('remove-applicant', applicantId)"
          />

          <TrustSection
            v-else-if="isTrust"
            ref="trustSectionRef"
            :all-individuals="props.allIndividuals"
            :initial-trust-details="initialTrustDetails"
            :initial-company-details="initialTrustCompanyDetails"
            :initial-directors="initialDirectors"
            :initial-trustees="initialTrustees"
            :is-primary-borrower="isPrimaryBorrower"
            :force-show-errors="props.forceShowErrors"
            @relationship-change="(individualId: string, newStatus: string, oldStatus?: string) => emit('relationship-change', individualId, newStatus, oldStatus)"
            @sync-applicant="(applicant: ApplicantEntry) => emit('sync-applicant', applicant)"
            @remove-applicant="(applicantId: string) => emit('remove-applicant', applicantId)"
          />

          <!-- Action buttons - hide for company and trust (buttons handled in their sections) -->
          <div v-if="!isCompany && !isTrust" ref="actionSection" class="mt-10 flex-shrink-0">
            <BxButton
              :id="`${props.modelValue.id}-borrower-save`"
              :data-test-id="`${props.modelValue.id}-borrower-save`"
              variant="secondary"
              @click="saveChanges()"
            >
              Save
            </BxButton>
            <BxButton
              v-if="modelValue.firstName"
              :data-test-id="`${props.modelValue.id}-borrower-cancel`"
              variant="link"
              class="ml-4"
              @click="cancelChanges()"
              >Cancel</BxButton
            >
            <BxButton
              v-else
              :data-test-id="`${props.modelValue.id}-borrower-cancel-new`"
              variant="link"
              class="ml-4"
              @click="openModal()"
              >Cancel</BxButton
            >
          </div>
        </div>
      </template>
    </BxExpander>
    <BxModal
      :open="showModal"
      :icon="TrashIcon"
      title="Remove borrower"
      confirm-label="Remove"
      cancel-label="Cancel"
      @close="showModal = false"
      @confirm="removeItem()"
    >
      Do you want to remove
      <span class="text-bridgit-green">{{ displayName || 'the new borrower' }}</span
      >?
    </BxModal>
  </div>
</template>

<style lang="scss" scoped>
.w-100 {
  width: 100%;
}
.max-w-500 {
  max-width: 500px;
}
</style>
